import React, { useCallback, useMemo, useState } from 'react';
import { Platform, View } from 'react-native';
import Svg, { Line } from 'react-native-svg';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import Button from 'design-system/Button';
import CardRow from 'design-system/CardRow';
import Modal from 'utils/packages/Modal';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { getAnnualDiscountPercentageFromStandard } from 'features/premium/annualDiscount';
import { Tier, TierPeriod } from 'features/premium/entities';
import CalculationDescription from 'features/premium/components/CalculationDescription/CalculationDescription';
import { buildPremiumModel } from 'features/premium/components/CalculationDescription/buildPremiumModel';
import { selectUserTier } from 'features/premium/selectors/tier';
import { useAppSelector } from 'store/hooks';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, formatDateForItem, formatTryFor0, } from 'utils/formatting';
import Links, { knownLinks } from 'utils/links';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { getThemeGradient } from 'features/premium/colors';
import useColors from 'hooks/useColors';
import PlanEmmaBasic from 'design-system/PlanEmmaBasic';
import PlanEmmaPlus from 'design-system/PlanEmmaPlus';
import PlanEmmaPro from 'design-system/PlanEmmaPro';
import PlanEmmaUltimate from 'design-system/PlanEmmaUltimate';
import { tierThemes } from 'features/premium/benefits';
import CreditsTooltip from 'features/premium/components/CreditsTooltip';
import ProRataTooltip from 'features/premium/components/ProRataTooltip';
const roundAmount = (amount) => Math.round(amount * 100) / 100;
/**
 * V2 of the partial refund breakdown: the annual discount and the promotion are
 * both taken off the standard price to give the new annual total, with the
 * refund shown separately below it.
 */
const PartialRefundRows = ({ currency, previewData, annualDiscountPercentage, dividerStyle, dottedDividerStyle, dottedDividerColor, }) => {
    const annualDiscount = roundAmount(Number(previewData.displayPrice) - Number(previewData.fullPrice));
    const promotion = previewData.percentageDiscount?.amount || previewData.fixedDiscount || 0;
    const newAnnualTotal = roundAmount(Number(previewData.fullPrice) - promotion);
    return (<>
      <CardRow testID="priceRow" title="Standard price" small smallPadding numberInfo info={formatAmount(previewData.displayPrice, currency, 2)}/>
      {annualDiscount > 0 ? (<CardRow testID="discountRow" small smallPadding title={annualDiscountPercentage
                ? `${annualDiscountPercentage}% annual discount`
                : 'Annual discount'} info={<Text Numbers-14>{formatAmount(-annualDiscount, currency, 2)}</Text>}/>) : null}
      {promotion ? (<CardRow testID="promotionRow" small smallPadding title={previewData.percentageDiscount?.amount
                ? `${previewData.percentageDiscount.percentage}% off promo`
                : 'Promotion'} info={<Text Numbers-14>{formatAmount(-promotion, currency, 2)}</Text>}/>) : null}

      <View style={dividerStyle}/>

      <CardRow smallPadding testID="newAnnualTotalRow" title={<Text Text-14>Your new annual total</Text>} info={formatAmount(newAnnualTotal, currency, 2)} numberInfo/>
      <Svg height="1.5" width="100%" style={dottedDividerStyle}>
        <Line x1="100%" y1="0" x2="0" y2="0" stroke={dottedDividerColor} strokeWidth="1" strokeDasharray="2, 2"/>
      </Svg>
      <CardRow smallPadding testID="refundRow" title={<Text Text-14>Refund today</Text>} info={formatAmount(previewData.refundableToday ?? 0, currency, 2)} numberInfo/>
    </>);
};
const PremiumSubscriptionPurchasePreviewModal = ({ tier, period, currency, previewData, onPressContinue, reopen, variant, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const [isFetching, setIsFetching] = useState(false);
    const pressContinue = useCallback(async () => {
        setIsFetching(true);
        await onPressContinue();
        setIsFetching(false);
    }, [onPressContinue]);
    const navigation = useMainStackNavigation();
    const handleTnc = useCallback(() => {
        if (Platform.OS === 'android') {
            Modal.hide();
            navigation.navigateDeprecated('Pdf', {
                url: knownLinks.paidPlanTncs,
                onClose: () => {
                    reopen();
                },
            });
        }
        else {
            Links.openKnownLink('paidPlanTncs');
        }
    }, [reopen]);
    const isRefundableAndPayTodayZero = !!previewData.refundableToday && previewData.payToday === 0;
    const themeGradient = useMemo(() => getThemeGradient(tier, colors)?.colors, [tier, colors]);
    const currentTier = useAppSelector(selectUserTier);
    const emmaProStatus = useAppSelector((store) => store.user.user.emmaProStatus);
    // V19 — a new subscription that begins a free trial. The buyer is not
    // subscribed yet (so `isOnFreeTrial`/`expiresOn` are falsy); the signal is
    // trial eligibility, and a trial preview defers the first charge (payToday 0).
    const startsTrial = !emmaProStatus.usedFreeTrial &&
        !!emmaProStatus.eligibleFreeTrialDays &&
        previewData.payToday === 0;
    // The annual saving on this purchase, derived from the same two prices the
    // discount row below is built from, so the preview cannot state one number
    // above the button and another in the description.
    const annualDiscountPercentage = period === TierPeriod.yearly
        ? getAnnualDiscountPercentageFromStandard(previewData.displayPrice, previewData.fullPrice)
        : undefined;
    const descriptionModel = useMemo(() => {
        let currentPeriod;
        if (emmaProStatus.period === 'yearly') {
            currentPeriod = TierPeriod.yearly;
        }
        else if (emmaProStatus.period === 'monthly') {
            currentPeriod = TierPeriod.monthly;
        }
        const trialDaysRemaining = emmaProStatus.isOnFreeTrial && emmaProStatus.expiresOn
            ? Math.max(differenceInCalendarDays(parseISO(emmaProStatus.expiresOn), new Date()), 0)
            : undefined;
        const previewWithRefund = {
            ...previewData,
            credit: previewData.isUpcomingPayment ? 0 : previewData.credit,
            refundableToday: previewData.refundableToday,
        };
        return buildPremiumModel(previewWithRefund, {
            tier,
            period,
            currency,
            currentTier,
            currentPeriod,
            trialDaysRemaining,
            startsTrial,
            annualDiscountPercentage,
        });
    }, [
        annualDiscountPercentage,
        currency,
        currentTier,
        emmaProStatus.expiresOn,
        emmaProStatus.isOnFreeTrial,
        emmaProStatus.period,
        period,
        previewData,
        startsTrial,
        tier,
    ]);
    const TierImage = useMemo(() => {
        switch (tier) {
            case Tier.free:
                return <PlanEmmaBasic size={rem(44)}/>;
            case Tier.plus:
                return <PlanEmmaPlus size={rem(44)}/>;
            case Tier.pro:
                return <PlanEmmaPro size={rem(44)}/>;
            case Tier.ultimate:
                return <PlanEmmaUltimate size={rem(44)}/>;
            default:
                return undefined;
        }
    }, [tier]);
    const Price = useMemo(() => {
        if (isRefundableAndPayTodayZero && period === TierPeriod.yearly) {
            return (<CardRow testID="priceRow" title="Price" small smallPadding numberInfo info={formatAmount(previewData.fullPrice, currency, 2)}/>);
        }
        return (<CardRow testID="priceRow" title="Standard price" small smallPadding numberInfo info={formatAmount(previewData.displayPrice, currency, 2)}/>);
    }, [
        currency,
        isRefundableAndPayTodayZero,
        period,
        previewData.displayPrice,
        previewData.fullPrice,
    ]);
    const PercentageDiscount = useMemo(() => {
        if (previewData.percentageDiscount?.amount) {
            return (<CardRow testID="discountRow" small smallPadding title={`${previewData.percentageDiscount.percentage}% off promo`} info={<Text Numbers-14>
              {formatAmount(-previewData.percentageDiscount.amount, currency, 2)}
            </Text>}/>);
        }
        if (!isRefundableAndPayTodayZero && period === TierPeriod.yearly) {
            const discount = Number(previewData.displayPrice) - Number(previewData.fullPrice);
            if (!discount || annualDiscountPercentage === undefined)
                return null;
            return (<CardRow testID="discountRow" small smallPadding title={`${annualDiscountPercentage}% annual discount`} info={<Text Numbers-14>{formatAmount(-discount, currency, 2)}</Text>}/>);
        }
        return null;
    }, [
        annualDiscountPercentage,
        currency,
        isRefundableAndPayTodayZero,
        period,
        previewData.displayPrice,
        previewData.fullPrice,
        previewData.percentageDiscount,
    ]);
    const FixedDiscount = useMemo(() => {
        if (!previewData.fixedDiscount)
            return null;
        return (<CardRow testID="promotionRow" small smallPadding title="Promotion" info={<Text Numbers-14>
            {formatAmount(-previewData.fixedDiscount, currency, 2)}
          </Text>}/>);
    }, [currency, previewData.fixedDiscount]);
    const ProRata = useMemo(() => {
        if (!previewData.prorationCredit)
            return null;
        return (<CardRow small smallPadding testID="proRataRow" title={<View style={styles.creditRowContainer}>
            <Text TextThin-14 Secondary style={styles.creditRowTooltip}>
              Pro-rata
            </Text>
            <ProRataTooltip />
          </View>} info={<Text Numbers-14>
            {formatAmount(-previewData.prorationCredit, currency, 2)}
          </Text>}/>);
    }, [
        currency,
        previewData.prorationCredit,
        styles.creditRowContainer,
        styles.creditRowTooltip,
    ]);
    const creditsApplied = useMemo(() => Math.round(previewData.credit * 100 - (previewData.prorationCredit ?? 0) * 100) / 100, [previewData.credit, previewData.prorationCredit]);
    const isCreditsGranted = creditsApplied < 0;
    const Credits = useMemo(() => {
        if (!creditsApplied || previewData.isUpcomingPayment)
            return null;
        return (<CardRow small smallPadding testID="creditRow" title={<View style={styles.creditRowContainer}>
            <Text TextThin-14 Secondary style={styles.creditRowTooltip}>
              Credits
            </Text>
            <CreditsTooltip />
          </View>} info={<Text Numbers-14>{formatAmount(-creditsApplied, currency, 2)}</Text>}/>);
    }, [
        creditsApplied,
        previewData.isUpcomingPayment,
        styles.creditRowContainer,
        styles.creditRowTooltip,
        currency,
    ]);
    return (<View>
      <View style={{
            alignItems: 'center',
            marginTop: rem(48),
        }}>
        {TierImage}
        <Spacer.H16 />
        <Text Text-20>{tierThemes[tier].name}</Text>
        <Text Text-16 Secondary>
          {period === TierPeriod.yearly ? 'Annual' : 'Monthly'} subscription
        </Text>
      </View>

      <Spacer h={20}/>

      {variant === 'partialRefundV2' ? (<PartialRefundRows currency={currency} previewData={previewData} annualDiscountPercentage={annualDiscountPercentage} dividerStyle={styles.divider} dottedDividerStyle={styles.dottedDivider} dottedDividerColor={colors.borders.strongDivider}/>) : (<>
          {Price}
          {PercentageDiscount}
          {FixedDiscount}
          {ProRata}
          {isCreditsGranted ? null : Credits}

          <View style={styles.divider}/>

          {previewData.isUpcomingPayment ? (<>
              <CardRow small smallPadding testID="upcomingRow" title={<Text Text-14>
                    {formatDateForItem(previewData.nextChargeDate, 'Pay')}
                  </Text>} info={formatAmount(previewData.nextChargePrice, currency, 2)} numberInfo/>
              <Svg height="1.5" width="100%" style={styles.dottedDivider}>
                <Line x1="100%" y1="0" x2="0" y2="0" stroke={colors.borders.strongDivider} strokeWidth="1" strokeDasharray="2, 2"/>
              </Svg>
            </>) : null}

          {isRefundableAndPayTodayZero ? (<CardRow smallPadding testID="refundRow" title={<Text Text-14>Refund</Text>} info={formatAmount(previewData.refundableToday, currency, 2)} numberInfo/>) : (<>
              <CardRow smallPadding testID="payTodayRow" title={<Text Text-14>Pay today</Text>} info={formatAmount(previewData.payToday, currency, 2)} numberInfo/>
              {isCreditsGranted ? Credits : null}
            </>)}
        </>)}

      <Spacer h={12}/>

      <CalculationDescription model={descriptionModel}/>

      <Text TextThin-14 Secondary centered style={styles.tncText}>
        {startsTrial
            ? `Try ${emmaProStatus.eligibleFreeTrialDays} days free. `
            : ''}
        Cancel anytime.{' '}
        <Text TextThin-14 Primary suppressHighlighting onPress={handleTnc}>
          T&Cs
        </Text>{' '}
        apply.
      </Text>

      <Button brand custom title={!emmaProStatus.isOnFreeTrial &&
            !emmaProStatus.active &&
            emmaProStatus.eligibleFreeTrialDays
            ? formatTryFor0(currency)
            : 'Continue'} onPress={pressContinue} isFetching={isFetching} gradientColors={themeGradient} style={styles.button}/>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    titleRow: {
        marginTop: rem(32),
    },
    tncText: {
        marginVertical: rem(16),
    },
    creditRowContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    creditRowTooltip: {
        marginRight: rem(8),
    },
    button: {
        marginHorizontal: rem(16),
    },
    divider: {
        height: 1,
        backgroundColor: colors.borders.strongDivider,
        marginVertical: rem(8),
        marginHorizontal: rem(16),
    },
    dottedDivider: {
        marginVertical: rem(20),
    },
}));
export default PremiumSubscriptionPurchasePreviewModal;
